import React, { FC, Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import urlJoin from 'url-join';
import { Alert, ArrowBackwardIcon, Asset, Button } from '@clearcover/atlas';
import { LoadingPage } from 'Shared/components';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';
import { useQuoteApi } from 'Shared/services/Api';
import {
  formatDateTimeFullUsingTimezone,
  formatMoney,
  formatDateShort,
  toTitleCase,
} from 'Shared/utils';
import { Body, ScreenHeader } from 'QuoteApp/components';
import { useAppContext } from 'QuoteApp/services/providers';
import styles from './styles.module.css';

type AppDataChangeType = {
  field: string;
  previousValue: null | string | boolean | number;
  currentValue: null | string | boolean | number;
};

type RateComparisonResponse = Array<{
  effective_date: string;
  quote_timestamp: string;
  policy_coverage_keys: Array<string>;
  vehicle_coverage_keys: Array<string>;
  payment_plans: Array<{
    premium_amount: number;
    plan_type: string;
  }>;
  app_data_changes: null | Array<AppDataChangeType>;
  rating_data: {
    policy_coverages: Record<string, any>;
    vehicles: Array<{
      vehicle_identification: string;
      coverages: Record<string, any>;
      vehicle_use: string;
    }>;
  };
}>;

const pretty = (value: string): string => {
  return toTitleCase(value.replace(/\.|_/g, ' '));
};

export const RateComparison: FC = () => {
  const { appId } = useAppContext();
  const [showMore, setShowMore] = useState<boolean>(false);
  const [{ data: responseData, error, loading }] =
    useQuoteApi<RateComparisonResponse>(
      { url: `insurance_applications/${appId}/compare_rates` },
      false,
      false
    );

  const policyCoverages = (responseData || [])[0]?.policy_coverage_keys;
  const vehicleCoverages = (responseData || [])[0]?.vehicle_coverage_keys;

  const vehicles = (responseData || []).reduce((acc: Array<string>, rate) => {
    const currVehicles = rate.rating_data.vehicles.map(
      (v) => v.vehicle_identification
    );
    return Array.from(new Set([...acc, ...currVehicles]));
  }, []);

  if (error) {
    return <Alert message={error.message} />;
  }

  if (loading) {
    return <LoadingPage />;
  }

  return responseData ? (
    <Body contentClassName={styles.bodyWrapper}>
      <Link
        to={urlJoin(QUOTE_APP_ROUTE, appId, '/summary')}
        className={styles.back}
      >
        <Asset
          description="Back"
          icon={ArrowBackwardIcon}
          height={14}
          width={14}
        />{' '}
        Back to Summary
      </Link>
      <ScreenHeader headerText="Rate Comparison Chart" />
      <div className={styles.wrapper}>
        <div className={styles.scrollableContainer}>
          <table className={styles.table}>
            <thead>
              <tr key="header-label-row">
                <th className={styles.label} key="header-label">
                  Quote Attempt
                </th>
                {responseData.map((_d, i) => {
                  return <th key={`header-${i}`}>{i + 1}</th>;
                })}
              </tr>
              <tr key="header-timestamp-row">
                <th className={styles.label} key="header-timestamp-label">
                  Timestamp
                </th>
                {responseData.map((d, i) => {
                  return (
                    <th key={`header-timestamp-${i}`}>
                      {formatDateTimeFullUsingTimezone(
                        d.quote_timestamp,
                        'America/Chicago'
                      )}
                    </th>
                  );
                })}
              </tr>
              <tr key="header-premiun-full-row">
                <th className={styles.label} key="header-premium-full-label">
                  Paid in Full
                </th>
                {responseData.map((d, i) => {
                  const premium = d.payment_plans.find(
                    (p) => p.plan_type === 'Paid in Full - Credit Card'
                  );
                  return (
                    <th key={`header-premium-full-${i}`}>
                      {formatMoney(premium?.premium_amount || 0)}
                    </th>
                  );
                })}
              </tr>
              <tr key="header-premiun-installments-row">
                <th
                  className={styles.label}
                  key="header-premium-installments-label"
                >
                  Pay 6 installments
                </th>
                {responseData.map((d, i) => {
                  const premium = d.payment_plans.find(
                    (p) => p.plan_type === 'Pay 6 installments - Credit Card'
                  );
                  return (
                    <th key={`header-premium-installments-${i}`}>
                      {formatMoney(premium?.premium_amount || 0)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr key="body-effective-date-row">
                <td className={styles.label} key="effective-date-label">
                  Effective Date
                </td>
                {responseData.map((d, i) => (
                  <td key={`${i}-effective-date`}>
                    {formatDateShort(d.effective_date)}
                  </td>
                ))}
              </tr>
              <tr key="body-diff-row">
                <td className={styles.label} key="diff-label">
                  Changes
                </td>
                {responseData.map((d, i) => {
                  const visibleWhenCollapsed = 5;
                  const expandable =
                    (d.app_data_changes || []).length > visibleWhenCollapsed;
                  return (
                    <td key={`diff-${i}`} className={styles.diff}>
                      {d.app_data_changes?.map((c, j) => {
                        return (
                          <AppDataChange
                            key={`${c.field}-${i}-${j}`}
                            change={c}
                            show={
                              (expandable && showMore) ||
                              j + 1 <= visibleWhenCollapsed
                            }
                          />
                        );
                      })}
                      {expandable && (
                        <Button
                          link
                          size="sm"
                          className={styles.showMore}
                          onClick={() => setShowMore(!showMore)}
                        >
                          {showMore ? 'Show less' : 'Show all'}
                        </Button>
                      )}
                    </td>
                  );
                })}
              </tr>
              <tr key="body-policy-coverages-row">
                <th className={styles.label}>Policy Coverages</th>
                <th colSpan={responseData.length} />
              </tr>
              {policyCoverages.map((key, i) => {
                return (
                  <tr key={`policy-coverages-${key}-${i}`}>
                    <td className={styles.label}>{pretty(key)}</td>
                    {responseData.map((d, j) => {
                      const coverage = d.rating_data.policy_coverages[key];
                      return <td key={`${key}-${i}-${j}-value`}>{coverage}</td>;
                    })}
                  </tr>
                );
              })}
              {vehicles.map((vehicle_identification, i) => {
                return (
                  <Fragment key={`${vehicle_identification}-header-row-${i}`}>
                    <tr key={`${vehicle_identification}-header-row`}>
                      <th className={styles.label}>Vehicle[{i}]</th>
                      {responseData.map((_d, j) => {
                        return (
                          <th key={`${vehicle_identification}-${i}-${j}`}>
                            {vehicle_identification}
                          </th>
                        );
                      })}
                    </tr>
                    {vehicleCoverages.map((key, j) => {
                      return (
                        <tr key={`${vehicle_identification}-${key}-${i}-${j}`}>
                          <td
                            className={styles.label}
                            key={`${vehicle_identification}-${i}-${j}-${key}-label`}
                          >
                            {pretty(key)}
                          </td>
                          {responseData.map((d, k) => {
                            const vehicle = d.rating_data.vehicles.find(
                              (v) =>
                                v.vehicle_identification ===
                                vehicle_identification
                            );
                            const coverage = vehicle?.coverages[key];
                            return (
                              <td
                                key={`${vehicle_identification}-${i}-${j}-${k}-${key}-value`}
                              >
                                {coverage || ''}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    <tr key={`${vehicle_identification}-vehicle-use-row`}>
                      <td
                        className={styles.label}
                        key={`${vehicle_identification}-vehicle-use-label`}
                      >
                        Vehicle Use
                      </td>
                      {responseData.map((d, j) => {
                        const vehicle = d.rating_data.vehicles.find(
                          (v) =>
                            v.vehicle_identification === vehicle_identification
                        );
                        return (
                          <td
                            key={`${vehicle_identification}-${i}-${j}-vehicle-use-value`}
                          >
                            {vehicle?.vehicle_use}
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Body>
  ) : null;
};

const AppDataChange: FC<{ change: AppDataChangeType; show: boolean }> = ({
  change,
  show,
}) => {
  const transformValue = (value: null | string | boolean | number) => {
    if (typeof value === 'string') {
      const date = formatDateTimeFullUsingTimezone(value, 'America/Chicago');
      return date === 'Invalid DateTime' ? value : date;
    }
    return value;
  };

  const prevValue = transformValue(change.previousValue);
  const currValue = transformValue(change.currentValue);

  return show ? (
    <div className={styles.diffWrapper}>
      <div className={styles.diffLabel}>{pretty(change.field)}</div>
      <div>{`${prevValue} > ${currValue}`}</div>
    </div>
  ) : null;
};
